<script setup>
import { computed, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TaccCompanyAPI from 'dashboard/api/taccCompany';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  selectedName: {
    type: String,
    default: '',
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const businesses = ref([]);
const searchQuery = ref('');
const isLoading = ref(false);

const normalizedValue = computed(() => String(props.modelValue || ''));

const filteredBusinesses = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return businesses.value;
  return businesses.value.filter(business =>
    business.name.toLowerCase().includes(query)
  );
});

const options = computed(() => {
  const list = [
    { value: '', label: 'No company' },
    ...filteredBusinesses.value.map(business => ({
      value: String(business.id),
      label: business.name,
    })),
  ];

  if (
    normalizedValue.value &&
    props.selectedName &&
    !list.some(option => String(option.value) === normalizedValue.value)
  ) {
    list.splice(1, 0, {
      value: normalizedValue.value,
      label: props.selectedName,
    });
  }

  return list;
});

const loadBusinesses = async () => {
  if (businesses.value.length || isLoading.value) return;
  isLoading.value = true;
  try {
    const { data } = await TaccCompanyAPI.getBusinesses();
    businesses.value = Array.isArray(data.businesses) ? data.businesses : [];
  } catch {
    businesses.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleOpen = () => {
  searchQuery.value = '';
  loadBusinesses();
};

const handleSearch = useDebounceFn(query => {
  searchQuery.value = query || '';
}, 200);

const handleSelect = value => {
  const id = String(value || '');
  const selected = businesses.value.find(
    business => String(business.id) === id
  );

  emit('select', {
    id,
    name: selected?.name || '',
    odooId: selected?.odooId || '',
  });
};
</script>

<template>
  <ComboBox
    :model-value="normalizedValue"
    :options="options"
    :display-label="selectedName"
    placeholder="Select company"
    search-placeholder="Search company..."
    class="[&>div>button]:h-8 [&>div>div_ul]:max-h-56"
    :class="{
      '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
        !isDetailsView,
      '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
    }"
    @open="handleOpen"
    @search="handleSearch"
    @update:model-value="handleSelect"
  />
</template>
